import { useEffect, useState } from "react";
import { Button, RadioGroup, Loader } from "@/common/components/ui";
import { CommonSelect } from "@/common/components/features";
const ProfilePlaceholder = "/assets/profile.png";
import PersonalDetailsService from "@/services/NPS/Stepper/APIs/PersonalDetailsService";
import { PersonalDetailsPayload } from "@/utils/types/NPS/Stepper/PersonalDetails";
import { CustomImageUpload } from "@/common/components/CustomImageUpload";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import { CustomFileUpload } from "@/common/components/CustomFileUpload";
interface StepPersonalDetailsProps {
  kycForm: any;
  setNextStep: (nextStep: string) => void;
  setIsNominationStep: (isNominationStep: boolean) => void;
  setInitialDetails: (initialDetails: any) => void;
  onNext: () => void;
  registrationDetails?: Registration | null;
}

const formatDate = (date: string): string => {
  if (!date) {
    return "";
  }
  const day = date.substring(0, 2); // Extract day
  const month = date.substring(2, 4); // Extract month
  const year = date.substring(4, 8); // Extract year
  return `${day}-${month}-${year}`;
};

const StepPersonalDetails: React.FC<StepPersonalDetailsProps> = ({
  kycForm,
  setNextStep,
  setInitialDetails,
  onNext,
  registrationDetails,
}) => {
  const [relation, setRelation] = useState("F");
  const [paperless, setPaperless] = useState(true);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [residentialStatus, setResidentialStatus] = useState("IN");
  const [countryOfBirth, setCountryOfBirth] = useState("IN");
  const [nationality, setNationality] = useState("IN");
  const [occupation, setOccupation] = useState("");
  const [otherOccupation, setOtherOccupation] = useState("");
  const [incomeRange, setIncomeRange] = useState("");
  const [pepStatus, setPepStatus] = useState("");
  const [pranMode, setPranMode] = useState("");
  const [signature, setSignature] = useState<string | null>("");

  const [formValues, setFormValues] = useState({
    firstName: kycForm?.fullName,
    dob: kycForm?.dob,
    mobile: kycForm?.mobile,
    email: kycForm?.email,
    pan: kycForm?.pan,
    spouseName: "",
    placeOfBirth: "",
    fatherName: "",
    motherName: "",
    certificateFile: "",
    orphan_url: "",
    address: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [photoText, setPhotoText] = useState<string>("");

  useEffect(() => {
    if (registrationDetails) {
      setSelectedTitle(registrationDetails.personalDtls.subTitle);
      setSelectedGender(registrationDetails.personalDtls.gender);
      setMaritalStatus(registrationDetails.personalDtls.maritalStatus);
      setResidentialStatus(registrationDetails.personalDtls.countryOfResidence);
      setCountryOfBirth(registrationDetails.personalDtls.countryOfBirth);
      setNationality(registrationDetails.personalDtls.countryOfResidence);
      setOccupation(registrationDetails.personalDtls.occupation);
      setOtherOccupation(registrationDetails.personalDtls.otherOccDetails);
      setIncomeRange(registrationDetails.personalDtls.incomeRange);
      setPepStatus(registrationDetails.personalDtls.politicallyExposed);
      setPepStatus(registrationDetails.personalDtls.relativePoliticalExposed);
      setPranMode(registrationDetails.personalDtls.epranWelcomePran);
      // setPhoto(registrationDetails.personalDtls.photo_url);
      setSignature(registrationDetails.personalDtls.signature_url);
      if (registrationDetails.personalDtls.mthFirstName) {
        setRelation("M");
      } else if (registrationDetails.personalDtls.fthFirstName) {
        setRelation("F");
      } else if (registrationDetails.personalDtls.certificateFile) {
        setRelation("N");
      }
      setPhotoText(registrationDetails.personalDtls.pPhotoText);
      setPhotoText(
        `data:image/png;base64,${registrationDetails.personalDtls.pPhotoText}`
      );
      setFormValues({
        firstName: registrationDetails.personalDtls.subFstName,
        dob: formatDate(registrationDetails.personalDtls.subDob),
        mobile: registrationDetails.personalDtls.mobile,
        email: registrationDetails.personalDtls.email,
        pan: registrationDetails.personalDtls.pan,
        spouseName: registrationDetails.personalDtls.spouseFirstName,
        placeOfBirth: registrationDetails.personalDtls.placeOfBirth || "",
        fatherName: registrationDetails.personalDtls.fthFirstName || "",
        motherName: registrationDetails.personalDtls.mthFirstName || "",
        certificateFile: registrationDetails.personalDtls.certificateFile || "",
        orphan_url: registrationDetails.personalDtls.orphan_url || "",
        address:
          registrationDetails.personalDtls.pAddrLine1 +
            " " +
            registrationDetails.personalDtls.pAddrLine2 +
            " " +
            registrationDetails.personalDtls.pAddrLine3 || "",
      });
    }
  }, [registrationDetails]);

  const handleChange = (field: string, value: any) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleSelectChange =
    (setter: React.Dispatch<React.SetStateAction<string>>, field: string) =>
    (value: string) => {
      setter(value);
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: "" }));
      }
    };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    if (e) {
      // Directly set the state based on the type (photo, signature, certificate)
      if (type === "photo") {
        setPhotoText(e as any); // Use the file URL for the photo preview
      } else if (type === "signature") {
        setSignature(e as any); // Use the file URL for the signature preview
      } else if (type === "orphan_url") {
        handleChange("orphan_url", e); // Assuming file name is enough for certificate field
      }

      // Clear the error for the selected file
      const errorField = type === "orphan_url" ? "orphan_url" : type;
      setErrors(prev => ({ ...prev, [errorField]: "" }));
    } else {
      if (type === "photo") {
        setPhotoText(e as any);
      } else if (type === "orphan_url") {
        handleChange("orphan_url", null);
      } else if (type === "signature") {
        setSignature(e as any);
      }
      setErrors(prev => ({ ...prev, [type]: "Please upload e-signature." }));
    }
  };

  const titleOptions = ["Shri", "Smt", "Kumari"];
  const genderOptions = [
    { label: "Male", value: "M" },
    { label: "Female", value: "F" },
    { label: "Transgender", value: "T" },
  ];
  const maritalOptions = [
    { label: "Married", value: "M" },
    { label: "Unmarried", value: "U" },
    { label: "Divorced", value: "D" },
    { label: "Widow/Widower", value: "W" },
  ];
  const residentialOptions = [
    { label: "Resident of India ", value: "IN" },
    { label: "Other", value: "OTHER" },
  ];
  const countryOptions = [
    { label: "India", value: "IN" },
    { label: "United States", value: "US" },
    { label: "United Kingdom", value: "GB" },
    { label: "Australia", value: "AU" },
    { label: "Canada", value: "CA" },
    { label: "Other", value: "OTHER" },
  ];
  const nationalityOptions = [
    { label: "Indian", value: "IN" },
    { label: "United States", value: "US" },
    { label: "United Kingdom", value: "GB" },
    { label: "Australia", value: "AU" },
    { label: "Canada", value: "CA" },
    { label: "Other", value: "OTHER" },
  ];
  const occupationOptions = [
    { label: "Public Sector", value: "07" },
    { label: "Private Sector", value: "03" },
    { label: "Self Employed", value: "02" },
    { label: "Professional", value: "08" },
    { label: "Homemaker", value: "10" },
    { label: "Others", value: "06" },
  ];
  const incomeOptions = [
    { label: "Upto ₹1 Lac", value: "01" },
    { label: "₹1 Lac to ₹5 Lac", value: "02" },
    { label: "₹5 Lac to ₹10 Lac", value: "03" },
    { label: "₹10 Lac to ₹25 Lac", value: "04" },
    { label: "₹25 Lac to ₹1 Cr", value: "05" },
    { label: "Above ₹1 Cr", value: "06" },
  ];
  const pepOptions = [
    { label: "Yes", value: "Y" },
    { label: "No", value: "N" },
  ];
  const pranOptions = [
    { label: "DIGITAL PRAN CARD & WELCOME KIT (CHARGES RS 18/-)", value: "1" },
    {
      label: "DIGITAL PRAN CARD & PHYSICAL WELCOME KIT (CHARGES RS 35/-)",
      value: "2",
    },
    {
      label: "PHYSICAL PRAN CARD & WELCOME KIT (CHARGES RS 40/-)",
      value: "3",
    },
  ];

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Title
    if (!selectedTitle) {
      newErrors.selectedTitle = "Please select a valid title";
    } else if (!["Shri", "Smt", "Kumari"].includes(selectedTitle)) {
      newErrors.selectedTitle = "Please select a valid title";
    }

    // Full Name – fetched, not editable, but we assume kycForm.name is valid

    // Gender
    if (!selectedGender) {
      newErrors.selectedGender = "Please select a valid gender";
    } else if (!["M", "F", "T"].includes(selectedGender.toUpperCase())) {
      newErrors.selectedGender = "Please select a valid gender";
    }

    // Marital Status
    if (!maritalStatus) {
      newErrors.maritalStatus = "Please select a valid marital status.";
    } else if (!["M", "U", "D", "W"].includes(maritalStatus)) {
      newErrors.maritalStatus = "Please select a valid marital status.";
    }

    // Spouse Name (conditional)
    if (maritalStatus === "M") {
      if (!formValues.spouseName.trim()) {
        newErrors.spouseName = "Please enter spouse name.";
      } else if (formValues.spouseName.trim().length > 30) {
        newErrors.spouseName = "Spouse name must be up to 30 characters.";
      }
    }

    // Place of Birth
    if (!formValues.placeOfBirth.trim()) {
      newErrors.placeOfBirth = "Please enter a valid place of birth.";
    } else if (!/^[a-zA-Z0-9\s]+$/.test(formValues.placeOfBirth)) {
      newErrors.placeOfBirth =
        "Place of birth must contain only alphanumeric and spaces.";
    } else if (formValues.placeOfBirth.length > 30) {
      newErrors.placeOfBirth = "Place of birth must be up to 30 characters.";
    }

    // Country of Birth
    if (!countryOfBirth) {
      newErrors.countryOfBirth = "Please select a valid country of birth.";
    } else if (
      !countryOptions.some(option => option.value === countryOfBirth)
    ) {
      newErrors.countryOfBirth = "Please select a valid country of birth.";
    }

    // Nationality (assuming same validation as country)
    if (!nationality) {
      newErrors.nationality = "Nationality is required.";
    } else if (
      !nationalityOptions.some(option => option.value === nationality)
    ) {
      newErrors.nationality = "Please select a valid nationality.";
    }

    // Occupation
    if (!occupation) {
      newErrors.occupation = "Please select a valid occupation.";
    } else if (
      !["02", "03", "04", "06", "07", "08", "10"].includes(occupation)
    ) {
      newErrors.occupation = "Please select a valid occupation.";
    }

    // Other Occupation (not in NPS spec – removed from validation)

    // Income Range (now required)
    if (!incomeRange) {
      newErrors.incomeRange = "Please select a valid income range.";
    } else if (!["01", "02", "03", "04", "05", "06"].includes(incomeRange)) {
      newErrors.incomeRange = "Please select a valid income range.";
    }

    // PEP Status (now required)
    if (!pepStatus) {
      newErrors.pepStatus = "Please select a valid option.";
    } else if (!["Y", "N"].includes(pepStatus)) {
      newErrors.pepStatus = "Please select a valid option.";
    }

    if (!signature) {
      newErrors.signature = "Please upload e-signature.";
    }
    // Note: PEP is optional per spec

    // PRAN Mode
    if (!pranMode) {
      newErrors.pranMode = "Please select PRAN receiving mode.";
    }

    // Relation-based fields
    if (relation === "F" && !formValues.fatherName.trim()) {
      newErrors.fatherName = "Father's name is required.";
    }
    if (relation === "M" && !formValues.motherName.trim()) {
      newErrors.motherName = "Mother's name is required.";
    }
    if (relation === "N" && !formValues.orphan_url) {
      newErrors.orphan_url = "Upload your Orphan Certificate.";
    }

    // Residential Status
    if (!residentialStatus) {
      newErrors.residentialStatus = "Residential status is required.";
    }

    if (occupation === "06") {
      if (!otherOccupation.trim()) {
        newErrors.otherOccupation = "Please specify your occupation.";
      } else if (otherOccupation.trim().length > 50) {
        // Optional: add max length
        newErrors.otherOccupation =
          "Occupation details must be under 50 characters.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasPersonalDetailsChanged = (payload: PersonalDetailsPayload) => {
    if (!registrationDetails?.personalDtls) {
      return true; // First-time create
    }

    const oldData = registrationDetails.personalDtls;

    for (const key in payload) {
      if ((payload as any)[key] !== (oldData as any)[key]) {
        return true; // Something changed
      }
    }

    return false; // No changes
  };

  const handleNext = async () => {
    if (!validate()) {
      return;
    }
    const payload: PersonalDetailsPayload = {
      subTitle: selectedTitle,
      placeOfBirth: formValues.placeOfBirth,
      countryOfBirth,
      countryOfResidence: "IN",
      nationality: "IN",
      maritalStatus: maritalStatus as "M" | "U" | "D" | "W",
      mobile: kycForm.mobile,
      email: formValues.email,
      fthFirstName: formValues.fatherName,
      mthFirstName: formValues.motherName,
      spouseFirstName: formValues.spouseName,
      pan: kycForm.pan,
      // dobProof: "150",
      displayNameFlag: relation as "F" | "M" | "N",
      epranWelcomePran: pranMode,
      modeOfAnnualSOT: paperless ? "E" : "P",
      signatureDocument: signature as string,
      occupation: occupation,
      relativePoliticalExposed: pepStatus,
      otherOccDetails: otherOccupation,
      incomeRange: incomeRange,
      politicallyExposed: "",
      orphan_url:
        relation === "M" || relation === "F"
          ? undefined
          : ((formValues?.orphan_url as any)[0]?.id ?? ""),
    };

    setIsLoading(true);
    try {
      let response;
      if (
        registrationDetails?.personalDtls.signature_url !== null &&
        registrationDetails?.personalDtls.signature_url !== ""
      ) {
        if (hasPersonalDetailsChanged(payload)) {
          response = await PersonalDetailsService.updatePersonalDetailsById(
            registrationDetails?.initialDetails?.ackId ?? "",
            payload
          );
        }
      } else {
        response = await PersonalDetailsService.createPersonalDetails(payload);
      }
      if (response?.success) {
        setNextStep(response?.registration?.next_step); // Set next step
        setInitialDetails(response?.registration?.initialDetails);

        // setIsNominationStep(response?.registration?.next_step === "nomination");
        onNext();
      }
    } catch (error) {
      console.error("❌ Registration failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <Loader message="Submitting your personal details..." />}
      <div className="space-y-8 bg-white py-8">
        <h2 className="mb-2 font-sans text-[20px] font-semibold text-[#127B8D] md:text-[24px]">
          Personal Details
        </h2>

        <div className="grid grid-cols-1 items-start gap-x-6 gap-y-3 md:grid-cols-3 lg:grid-cols-4">
          <div className="col-span-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-1">
              <label className="mb-1 block font-sans text-[16px] font-semibold text-[#707070]">
                Title <span className="text-red-500">*</span>
              </label>
              <CommonSelect
                options={titleOptions}
                value={selectedTitle}
                onChange={handleSelectChange(setSelectedTitle, "selectedTitle")}
                placeholder="Title"
                required
                errorMessage={errors.selectedTitle}
              />
            </div>

            <div className="sm:col-span-1 lg:col-span-2">
              <label className="mb-1 block font-sans text-[16px] font-semibold text-[#707070]">
                Full Name (As per Aadhaar Card)
              </label>
              <input
                type="text"
                value={formValues?.firstName ?? ""}
                readOnly
                className="w-full cursor-not-allowed rounded-md border border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none"
              />
            </div>

            <div className="sm:col-span-1">
              <label className="mb-1 block font-sans text-[16px] font-semibold text-[#707070]">
                Birth Date (As per Aadhaar Card)
              </label>
              <input
                type="text"
                value={formValues?.dob ?? ""}
                readOnly
                className="w-full cursor-not-allowed rounded-md border border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none"
              />
              {/* Assuming dob is already validated in kycForm */}
            </div>

            <div className="col-span-1 sm:col-span-2 lg:col-span-4">
              <label className="mb-1 block font-sans text-[16px] font-semibold text-[#707070]">
                Address (As per Aadhaar Card)
              </label>
              <textarea
                value={formValues?.address ?? ""}
                readOnly
                rows={2}
                className="w-full cursor-not-allowed resize-none rounded-md border border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#2F363F] outline-none"
              />
            </div>
          </div>

          <div className="flex flex-col items-center justify-start gap-2">
            <label className="font-sans text-[16px] font-semibold text-[#707070]">
              Upload Photo & Signature (As per Aadhaar Card)
            </label>
            <div className="flex flex-row flex-nowrap items-center gap-2">
              <div className="relative">
                <img
                  src={photoText ?? ProfilePlaceholder}
                  alt="Profile"
                  className="h-[110px] w-[110px] rounded-md object-cover"
                />
              </div>

              <div>
                {/* <img
                  src={signature ?? SignaturePlaceholder}
                  alt="Signature"
                  className="h-[60px] w-[80px] object-contain"
                />
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 cursor-pointer opacity-0"
                  onChange={e => handleFileChange(e, "signature")}
                /> */}
                <CustomImageUpload
                  boxWidth={110}
                  boxHeight={110}
                  isEditMode
                  value={signature}
                  initialUrl={signature as string}
                  onChange={(e: any) => handleFileChange(e, "signature")}
                  accept="image/*"
                />
                {errors.signature && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.signature}
                  </p>
                )}
              </div>
            </div>
            <p className="text-center font-sans text-[14px] font-semibold text-[#707070]">
              Upload your e-signature in .jpg with 2KB and 5MB only.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-3">
          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Gender (As per Aadhaar Card){" "}
              <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={genderOptions}
              value={selectedGender}
              disabled
              onChange={handleSelectChange(setSelectedGender, "selectedGender")}
              placeholder="Gender"
              errorMessage={errors.selectedGender}
            />
          </div>

          <div>
            <label className="mb-4 font-sans text-[16px] font-semibold text-[#707070]">
              Marital Status <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={maritalOptions}
              value={maritalStatus}
              onChange={value => {
                handleSelectChange(setMaritalStatus, "maritalStatus")(value);
                if (value !== "M") {
                  // Clear spouse name if not married
                  setFormValues(prev => ({ ...prev, spouseName: "" }));
                  // Optional: clear any spouse-related validation error
                  if (errors.spouseName) {
                    setErrors(prev => ({ ...prev, spouseName: "" }));
                  }
                }
              }}
              placeholder="Marital Status"
              errorMessage={errors.maritalStatus}
            />
          </div>

          <div className="flex flex-col">
            <label className="font-sans text-[16px] font-semibold text-[#707070]">
              Spouse’s Name
              {maritalStatus === "M" && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              disabled={maritalStatus !== "M"}
              value={formValues.spouseName}
              onChange={e => handleChange("spouseName", e.target.value)}
              className={`w-full rounded-md border ${
                errors.spouseName ? "border-red-500" : "border-[#DFE7EC]"
              } px-3 py-2.5 font-sans text-[16px] font-semibold outline-none focus:border-[#66A0CD] ${
                maritalStatus === "M"
                  ? "bg-white focus:border-[#66A0CD]"
                  : "cursor-not-allowed border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] text-[#2F363F] placeholder-[#2F363F]"
              }`}
            />
            {errors.spouseName && (
              <p className="mt-1 text-sm text-red-500">{errors.spouseName}</p>
            )}
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Residential Status
              <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={residentialOptions}
              value={residentialStatus}
              onChange={value =>
                handleSelectChange(
                  setResidentialStatus,
                  "residentialStatus"
                )(value)
              }
              placeholder="Residential Status"
              disabled
              errorMessage={errors.residentialStatus}
            />
          </div>

          <div className="flex flex-col">
            <label className="font-sans text-[16px] font-semibold text-[#707070]">
              Place of Birth<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Place of Birth"
              value={formValues.placeOfBirth}
              onChange={e => handleChange("placeOfBirth", e.target.value)}
              className={`w-full rounded-md border ${
                errors.placeOfBirth ? "border-red-500" : "border-[#DFE7EC]"
              } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
            />
            {errors.placeOfBirth && (
              <p className="mt-1 text-sm text-red-500">{errors.placeOfBirth}</p>
            )}
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Country of Birth <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={countryOptions}
              value={countryOfBirth}
              onChange={handleSelectChange(setCountryOfBirth, "countryOfBirth")}
              placeholder="Country of Birth"
              disabled
              errorMessage={errors.countryOfBirth}
            />
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Nationality <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={nationalityOptions}
              value={nationality}
              onChange={handleSelectChange(setNationality, "nationality")}
              placeholder="Nationality"
              disabled
              errorMessage={errors.nationality}
            />
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Occupation <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={occupationOptions}
              value={occupation}
              onChange={value => {
                handleSelectChange(setOccupation, "occupation")(value);
                // Clear otherOccupation when not "06"
                if (value !== "06") {
                  setOtherOccupation("");
                  if (errors.otherOccupation) {
                    setErrors(prev => ({ ...prev, otherOccupation: "" }));
                  }
                }
              }}
              placeholder="Occupation"
              errorMessage={errors.occupation}
            />
          </div>

          {/* Other Occupation Details */}
          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Enter occupation (If other)
              {occupation === "06" && <span className="text-red-500">*</span>}
            </label>
            <input
              value={otherOccupation}
              onChange={e => setOtherOccupation(e.target.value)}
              disabled={occupation !== "06"}
              className={`w-full rounded-md border ${
                errors.otherOccupation ? "border-red-500" : "border-[#DFE7EC]"
              } px-3 py-2.5 font-sans text-[16px] font-semibold ${
                occupation === "06"
                  ? "cursor-text bg-white text-[#2F363F] placeholder-[#BECAD0]"
                  : "cursor-not-allowed border-[#DFE7EC] bg-[rgba(112,112,112,0.3)] text-[#2F363F] placeholder-[#2F363F]"
              } outline-none focus:outline-none ${
                occupation === "06" ? "focus:border-[#66A0CD]" : ""
              }`}
            />
            {errors.otherOccupation && (
              <p className="mt-1 text-sm text-red-500">
                {errors.otherOccupation}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Income Range (Per annum) <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={incomeOptions}
              value={incomeRange}
              onChange={handleSelectChange(setIncomeRange, "incomeRange")}
              placeholder="Income Range"
              errorMessage={errors.incomeRange} // Dynamic error message
            />
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Are you Politically Exposed Person?{" "}
              <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={pepOptions}
              value={pepStatus}
              onChange={handleSelectChange(setPepStatus, "pepStatus")}
              placeholder="PEP Status"
              errorMessage={errors.pepStatus}
            />
          </div>

          <div>
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              I would like to receive my PRAN as{" "}
              <span className="text-red-500">*</span>
            </label>
            <CommonSelect
              options={pranOptions}
              value={pranMode}
              onChange={handleSelectChange(setPranMode, "pranMode")}
              placeholder="PRAN Mode"
              errorMessage={errors.pranMode}
            />
          </div>
        </div>

        <div className="mt-5">
          <RadioGroup
            options={[
              { key: "F", label: "Father's name" },
              { key: "M", label: "Mother's name" },
              { key: "N", label: "I am an orphan" },
            ]}
            value={relation}
            onChange={value => {
              setRelation(value);
              // Clear errors for all relation-based fields when changing selection
              setErrors(prev => ({
                ...prev,
                fatherName: "",
                motherName: "",
                orphan_url: "",
              }));
              // Clear form values when switching
              if (value === "F") {
                setFormValues(prev => ({
                  ...prev,
                  motherName: "",
                  orphan_url: "",
                }));
              } else if (value === "M") {
                setFormValues(prev => ({
                  ...prev,
                  fatherName: "",
                  orphan_url: "",
                }));
              } else if (value === "N") {
                setFormValues(prev => ({
                  ...prev,
                  fatherName: "",
                  motherName: "",
                }));
              }
            }}
          />
        </div>

        {relation === "F" && (
          <div className="mt-2 flex flex-col">
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Father&apos;s Name on PRAN Card
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Father’s Name on PRAN Card"
              value={formValues.fatherName}
              onChange={e => handleChange("fatherName", e.target.value)}
              className={`w-full rounded-md border ${
                errors.fatherName ? "border-red-500" : "border-[#DFE7EC]"
              } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
            />
            {errors.fatherName && (
              <p className="mt-1 text-sm text-red-500">{errors.fatherName}</p>
            )}
          </div>
        )}

        {relation === "M" && (
          <div className="mt-2 flex flex-col">
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Mother’s Name on PRAN Card<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Mother’s Name on PRAN Card"
              value={formValues.motherName}
              onChange={e => handleChange("motherName", e.target.value)}
              className={`w-full rounded-md border ${
                errors.motherName ? "border-red-500" : "border-[#DFE7EC]"
              } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
            />
            {errors.motherName && (
              <p className="mt-1 text-sm text-red-500">{errors.motherName}</p>
            )}
          </div>
        )}

        {relation === "N" && (
          <div className="mt-2 flex flex-col">
            <label className="mb-1 font-sans text-[16px] font-semibold text-[#707070]">
              Upload Certificate<span className="text-red-500">*</span>
            </label>
            <div className="relative flex items-center">
              {/* <input
                type="file"
                accept=".pdf,.jpg,.png"
                className="absolute inset-0 cursor-pointer opacity-0"
                onChange={e => handleFileChange(e, "certificate")}
              />
              <input
                type="text"
                readOnly
                value={formValues.certificateFile ?? ""}
                placeholder="Upload Certificate"
                className={`w-full rounded-md border ${
                  errors.certificateFile ? "border-red-500" : "border-[#DFE7EC]"
                } bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]`}
              /> */}
              <CustomFileUpload
                value={formValues.orphan_url as any}
                onChange={(e: any) => handleFileChange(e, "orphan_url")}
                // placeholder="Upload Certificate"
                action="/api/upload-files"
                allowedTypes="imageAndPdf"
                // className="w-full rounded-md border border-[#DFE7EC] bg-white px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD]"
              />
              {/* <span className="absolute right-3 text-[#707070]">📤</span> */}
            </div>
            {errors.orphan_url && (
              <p className="mt-1 text-sm text-red-500">{errors.orphan_url}</p>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <input
            type="checkbox"
            checked={paperless}
            onChange={e => setPaperless(e.target.checked)}
            className="h-5 w-5 accent-[#000]"
          />
          <label className="font-sans text-[16px] font-semibold text-[#707070] md:text-[18px]">
            Go paperless : Email my Annual Statements
          </label>
        </div>

        <div className="mt-6 flex justify-center gap-3 sm:justify-end md:gap-5">
          {/* <Button
            type="button"
            onClick={onPrev}
            className="h-[50px] rounded-md bg-[#F5F5F5] px-8 py-2 font-sans text-[18px] font-semibold text-[#2F363F] hover:bg-[#f5f5f5] hover:opacity-95"
          >
            Back
          </Button> */}

          <Button
            type="button"
            onClick={handleNext}
            className="h-[50px] rounded-md bg-gradient-to-r from-[#127B8D] to-[#10B981] px-8 py-2 font-sans text-[18px] font-semibold tracking-[1px] text-white hover:opacity-95"
          >
            Continue
          </Button>
        </div>
      </div>
    </>
  );
};

export default StepPersonalDetails;
